// One more issue from the same call, as a card under the main ticket.
//
// The (+) on the create screen adds one of these; each is a full ticket — subject,
// message, form and its fields, priority, owner, queue, tags, device — filed as a child
// of the main ticket when everything is created together (child-tickets.js). What is NOT
// here is the caller: same call, same caller, read from the main ticket at submit time.
//
// Every input id is prefixed with the child's key, because the main ticket's Composer
// and Classification own the bare ids (`ticket-subject`, `ticket-form`, …) and two
// elements sharing an id would send a label's click — and a screen reader — to the
// wrong one.
//
// The form, queue and owner a child shows may only be the default (its `touched` says which
// the agent chose); changing one here makes it a choice, saved and filed as such.

import CustomFields from './CustomFields.jsx';
import TagInput from './TagInput.jsx';
import { setChildField } from './child-tickets.js';

const PRIORITIES = [
  ['low', 'Low'],
  ['normal', 'Normal'],
  ['high', 'High'],
  ['urgent', 'Urgent'],
];

const BASE_STATUSES = [
  ['new', 'New'],
  ['open', 'Open'],
  ['pending', 'Pending'],
  ['on_hold', 'On hold'],
  ['solved', 'Solved'],
  ['closed', 'Closed'],
];

export default function ChildTicketCard({
  child,
  index,
  config,
  user,
  errors = {},
  onChange,
  onRemove,
  subjectRef,
}) {
  const id = (name) => `child-${child.key}-${name}`;
  const setComposer = (key, value) => onChange({ ...child, composer: { ...child.composer, [key]: value } });
  // Choosing a form, queue or owner here marks it as this child's own (child-tickets.js).
  const setField = (key, value) => onChange(setChildField(child, key, value));

  const selectedForm = config.forms.find((form) => form.id === child.fields.form_id) ?? null;
  const formFields = selectedForm?.fields ?? [];
  const customErrors = {};
  for (const [key, message] of Object.entries(errors)) {
    if (key.startsWith('custom.')) customErrors[key.slice(7)] = message;
  }
  const isNote = child.composer.is_internal_note;
  const statusValue = child.fields.status_id ? `named:${child.fields.status_id}` : `base:${child.fields.status}`;

  return (
    <section
      className="rounded-lg border border-line bg-surface p-4"
      aria-labelledby={id('heading')}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 id={id('heading')} className="text-sm font-semibold text-ink">
          Also from this call <span className="font-normal text-ink-muted">· child ticket {index + 1}</span>
        </h2>
        <button type="button" className="text-xs text-ink-secondary hover:underline" onClick={onRemove}>
          Remove
        </button>
      </div>

      <div className="mt-3">
        <label className="field-label" htmlFor={id('subject')}>
          Subject <span className="text-red-600 dark:text-red-400">*</span>
        </label>
        <input
          id={id('subject')}
          ref={subjectRef}
          className="field-input"
          autoComplete="off"
          aria-invalid={Boolean(errors.subject)}
          value={child.composer.subject}
          onChange={(event) => setComposer('subject', event.target.value)}
        />
        {errors.subject && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{errors.subject}</p>}
      </div>

      <div className="mt-3">
        <div role="group" aria-label="Message type" className="flex gap-1">
          <button
            type="button"
            aria-pressed={isNote}
            onClick={() => setComposer('is_internal_note', true)}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
              isNote ? 'bg-note-accent text-note-on-accent ring-1 ring-note-line' : 'bg-sunken text-ink-secondary hover:bg-line hover:text-ink'
            }`}
          >
            Internal note
          </button>
          <button
            type="button"
            aria-pressed={!isNote}
            disabled={!user.can_send_customer_email}
            onClick={() => setComposer('is_internal_note', false)}
            title={user.can_send_customer_email ? undefined : 'Your role cannot send customer-facing email.'}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              !isNote ? 'bg-reply-strong text-reply-ink ring-1 ring-reply-accent' : 'bg-sunken text-ink-secondary hover:bg-line hover:text-ink'
            }`}
          >
            Public reply
          </button>
        </div>
        <div
          className={`mt-2 rounded-md border-l-8 p-2 ring-inset ${
            isNote ? 'border-note-accent bg-note ring-1 ring-note-line dark:ring-2' : 'border-reply-accent bg-reply-strong ring-1 ring-reply-accent'
          }`}
        >
          <label
            className={`mb-1 block text-xs font-semibold ${isNote ? 'text-note-ink' : 'text-reply-ink'}`}
            htmlFor={id('body')}
          >
            {isNote ? 'Internal note: stays inside the team' : 'Public reply: emailed to the customer'}
          </label>
          <textarea
            id={id('body')}
            rows={4}
            className="block w-full rounded-md border-0 bg-surface px-3 py-2 text-sm text-ink ring-1 ring-inset ring-line focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-400"
            placeholder="What the caller reported about this issue, and what was done."
            value={child.composer.body_text}
            onChange={(event) => setComposer('body_text', event.target.value)}
          />
        </div>
        {errors.message && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{errors.message}</p>}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor={id('form')}>Form</label>
          <select
            id={id('form')}
            className="field-input"
            value={child.fields.form_id}
            onChange={(event) => setField('form_id', event.target.value)}
          >
            <option value="">-</option>
            {config.forms.map((form) => (
              <option key={form.id} value={form.id}>{form.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor={id('priority')}>Priority</label>
          <select
            id={id('priority')}
            className="field-input"
            value={child.fields.priority}
            onChange={(event) => setField('priority', event.target.value)}
          >
            {PRIORITIES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <CustomFields
          fields={formFields}
          values={child.fields.custom_fields}
          errors={customErrors}
          onChange={(next) => setField('custom_fields', next)}
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor={id('assignee')}>Assignee</label>
          <select
            id={id('assignee')}
            className="field-input"
            value={child.fields.assigned_user_id}
            onChange={(event) => setField('assigned_user_id', event.target.value)}
          >
            <option value="">Unassigned</option>
            {config.assignees.map((option) => (
              <option key={option.id} value={option.id}>{option.display_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor={id('queue')}>Queue</label>
          <select
            id={id('queue')}
            className="field-input"
            value={child.fields.queue_id}
            onChange={(event) => setField('queue_id', event.target.value)}
          >
            <option value="">Default</option>
            {config.queues.map((queue) => (
              <option key={queue.id} value={queue.id}>{queue.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <TagInput
            value={child.fields.tags}
            onChange={(next) => setField('tags', next)}
            suggestions={config.tags}
          />
        </div>
        <div>
          <label className="field-label" htmlFor={id('device')}>Device serial</label>
          <input
            id={id('device')}
            className="field-input"
            autoComplete="off"
            value={child.fields.device_serial}
            onChange={(event) => setField('device_serial', event.target.value)}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <label className="text-xs font-medium text-ink-secondary" htmlFor={id('status')}>
          Create as
        </label>
        <select
          id={id('status')}
          className="field-input w-44 py-1.5 text-sm"
          value={statusValue}
          onChange={(event) => {
            const [kind, value] = event.target.value.split(':');
            if (kind === 'base') {
              onChange({ ...child, fields: { ...child.fields, status: value, status_id: null } });
              return;
            }
            const named = config.statuses.find((status) => status.id === value);
            if (named) {
              onChange({ ...child, fields: { ...child.fields, status: named.base_status, status_id: named.id } });
            }
          }}
        >
          {BASE_STATUSES.map(([value, label]) => (
            <option key={value} value={`base:${value}`}>{label}</option>
          ))}
          {config.statuses.length > 0 && (
            <optgroup label="Named statuses">
              {config.statuses.map((status) => (
                <option key={status.id} value={`named:${status.id}`}>
                  {status.name} ({status.base_status.replace('_', ' ')})
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <span className="text-xs text-ink-muted">Same caller as the main ticket. Worked and closed on its own.</span>
      </div>
    </section>
  );
}
